import { useState } from 'react';

import {
  ArrowButton, Button, DatePicker,
  TextField, Select, Modal, Typography, InfoPanel
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as InfoIcon } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import { useValidation } from '@king-ict/eupisi-ui/hooks';
import { CodeBook } from '@king-ict/eupisi-ui/models';
import { codeBookToSelectData, nameof } from '@king-ict/eupisi-ui/utils';
import { Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import { createProgram, editProgram } from '../../api/nisooApi';
import StaticField from '../../components/StaticField';
import { useInput } from "../../hooks";
import { ProgramNameValidationScheme, QualificationType } from '../../models/CodeBook';
import { createProgramMinisterDetailInitial, ProgramCodeBook, programInitial, ProgramType } from '../../models/Program';
import { RoleEnum } from "../../models/User/RoleEnum";
import {
  useGetProgramTypesQuery,
  useGetEducationSectorsQuery,
  useGetEducationSubSectorsQuery,
  useGetEnrollmentConditionsQuery,
  useGetFormsOfTeachingQuery,
  useGetKnowledgeChecksQuery,
  useGetLanguagesQuery,
  useGetQualificationTypesQuery,
  useGetQualificationTypesByProgramQuery
}
  from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { codebookRouteProgramName } from '../../routes/routes';
import { getErrorForPath as resolveErrorForPath } from "../../utils/validation/getErrorForPath";
import { ProgramNameMinisterFieldsEdit, upsertOtherList } from "./programNameMinister";
import styles from "./RequestCodeBook.module.scss";

type LocationState = {
  programName?: ProgramCodeBook;
};

export default function CodeBookProgramNameEdit(): JSX.Element {
  const history = useHistory();
  const { handleChange, handleNestedChange } = useInput();
  const location = useLocation<LocationState>();
  const { t } = useTranslation();
  const { data: userSession } = useGetUserSessionQuery();

  const getProgramInitial = () => {
    return location.state?.programName ?? programInitial();
  };

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [programName, setProgramName] = useState<ProgramCodeBook>(getProgramInitial());
  const effectiveProgramTypeId = programName.programWithMinisterResolution
    ? ProgramType.ProgramWithMinisterResolution
    : programName.programTypeId;
  const { data: programTypes } = useGetProgramTypesQuery();
  const { data: educationSectors } = useGetEducationSectorsQuery();
  const { data: educationSubSectors } = useGetEducationSubSectorsQuery(
    programName.educationSectorId!,
    {
      skip: !programName.educationSectorId
    }
  );
  const { data: qualificationTypesByProgram } = useGetQualificationTypesByProgramQuery(effectiveProgramTypeId!, {
    skip: effectiveProgramTypeId == null || !!programName.programWithMinisterResolution,
  });
  const { data: allQualificationTypes } = useGetQualificationTypesQuery(undefined, {
    skip: !programName.programWithMinisterResolution,
  });
  const { data: formsOfTeaching } = useGetFormsOfTeachingQuery(undefined);
  const { data: knowledgeChecks } = useGetKnowledgeChecksQuery(undefined);
  const { data: enrollmentConditions } = useGetEnrollmentConditionsQuery(undefined);
  const [hkoLevels, setHkoLevels] = useState<CodeBook[]>([]);

  const isProgramEditMode = programName && programName.id > 0;
  const isHelpdesk = userSession?.roleId === RoleEnum.Helpdesk;
  const isMinisterProgramReadonlyForUser = !!programName.programWithMinisterResolution && !isHelpdesk;
  const { data: languages } = useGetLanguagesQuery();

  const [errors, doValidations] = useValidation({
    data: programName,
    validationSchema: ProgramNameValidationScheme,
  });

  const filteredProgramTypes = (): CodeBook[] => {
    if (!programTypes?.length) {
      return [];
    }

    return programTypes.filter(
      (pt: CodeBook) =>
        pt.id === ProgramType.TrainingPrograms ||
        pt.id === ProgramType.CraftsAndArtsTrainingPrograms ||
        pt.id === ProgramType.CraftsSpecializationPrograms ||
        pt.id === ProgramType.RefinementPrograms ||
        pt.id === ProgramType.NewVocationalCurriculum,
    );
  };

  const handleProgramTypeChange = (event: any) => {
    const programTypeId = event.target.value;

    setProgramName((prev) => ({
      ...prev,
      programTypeId: programTypeId,
      qualificationTypeId: 0,
      hkoLevelId: 0
    }));
  };

  const handleQualificationTypeChange = (event: any) => {
    const qualificationTypeId = event.target.value;
    const selected = qualificationTypesByProgram?.find((qt) => qt.id === qualificationTypeId);
    const hkoLevel = selected ? [selected.hkoLevel] : [];

    setHkoLevels(hkoLevel);

    const [onlyHkoLevel] = hkoLevel;
    const hkoLevelId = hkoLevel.length === 1 ? onlyHkoLevel.id : 0;
    const hkoLevelName = hkoLevelId > 0 && hkoLevel.length === 1 ? onlyHkoLevel.name : "";

    const educationSubSectorId =
      qualificationTypeId === QualificationType.MicroQualification ? 0 : programName.educationSubSectorId;

    setProgramName((prev) => ({
      ...prev,
      qualificationTypeId: qualificationTypeId,
      hkoLevelName: prev.programWithMinisterResolution ? "" : hkoLevelName,
      hkoLevelId: prev.programWithMinisterResolution ? null : hkoLevelId,
      educationSubSectorId: educationSubSectorId
    }));
  };

  const handleSectorChange = (event: any) => {
    const educationSectorId = event.target.value;

    setProgramName((prev) => ({
      ...prev,
      educationSectorId: educationSectorId,
      educationSubSectorId: 0
    }));
  }

  const onMinisterMultiSelectChange = (
    ids: number[],
    otherId: number,
    idsField: "formOfTeachingIds" | "knowledgeCheckIds" | "enrollmentConditionIds",
    otherField: "otherFormsOfTeaching" | "otherKnowledgeChecks" | "otherEnrollmentConditions",
  ) => {
    setProgramName((prev) => ({
      ...prev,
      ministerDetail: {
        ...(prev.ministerDetail ?? createProgramMinisterDetailInitial()),
        [idsField]: ids,
        [otherField]: upsertOtherList(ids, otherId, prev.ministerDetail?.[otherField]),
      },
    }));
  };

  const getErrorForPath = (path: string): string | undefined => resolveErrorForPath(errors, path);

  const handleMinisterDecisionChange = (event: any) => {
    const checked = !!event.target.checked;
    setProgramName((prev) => ({
      ...prev,
      programWithMinisterResolution: checked,
      programTypeId: checked ? ProgramType.ProgramWithMinisterResolution : prev.programTypeId,
      hkoLevelId: checked ? null : prev.hkoLevelId,
      ministerDetail: checked
        ? (prev.ministerDetail ?? createProgramMinisterDetailInitial())
        : null,
    }));
  };

  const saveChanges = () => {
    if (isMinisterProgramReadonlyForUser) {
      return;
    }
    const isValid = doValidations();
    if (!isValid) {
      setShowValidationBanner(true);
      return;
    }
    setShowValidationBanner(false);
    setOpenConfirmModal(true);
  };

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  };

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    setShowValidationBanner(false);

    const requestToSave: ProgramCodeBook = {
      ...programName,
      dateTo: programName.dateTo
        ? new Date(Date.UTC(programName.dateTo.getFullYear(), programName.dateTo.getMonth(), programName.dateTo.getDate()))
        : undefined,
    };

    if (requestToSave.id) {
      await editProgram(requestToSave.id, requestToSave);
      navigateToDetails();
    }
    else {
      await createProgram(requestToSave);
      navigateToDetails();
    }
  }

  const cancel = () => {
    if (programName.id) {
      navigateToDetails()
    } else {
      navigateToList();
    }
  };

  const navigateToDetails = () => {
    history.push(
      codebookRouteProgramName(),
      {
        page: "details",
        programName: programName
      }
    );
  }

  const navigateToList = () => {
    history.push(
      codebookRouteProgramName()
    );
  }

  const getQualificationTypesSelectData = () => {
    const sourceQualificationTypes = programName.programWithMinisterResolution
      ? allQualificationTypes?.filter(
        (qt) =>
          qt.id === QualificationType.CompleteQualification ||
          qt.id === QualificationType.PartialQualification ||
          qt.id === QualificationType.MicroQualification,
      )
      : qualificationTypesByProgram;

    if (!sourceQualificationTypes) {
      return codeBookToSelectData(undefined);
    }

    const distinctQualificationTypes = sourceQualificationTypes.reduce<CodeBook[]>((acc, qt) => {
      if (acc.some((existing) => existing.id === qt.id)) {
        return acc;
      }
      acc.push({ id: qt.id, name: qt.name });
      return acc;
    }, []);

    return codeBookToSelectData(distinctQualificationTypes);
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <ArrowButton
            backwards
            ariaLabel=""
            onClick={navigateToList}
          />
          <span className={styles.backButtonCaption}>
            {t("codeBook.navigateToCodeBook")}
          </span>

        </Col>
      </Row>
      {showValidationBanner && (
        <Row className="mt-3">
          <Col md={12}>
            <InfoPanel
              color="danger"
              message={t("validations.checkRequiredFields")}
              fullWidth
            />
          </Col>
        </Row>
      )}
      <Row className={styles.title}>
        <Col md={6}>
          <h3 className={styles.titleWithTooltip}>{t("codeBook.programName")}</h3>
          <span className="text-primary" title={t("codeBook.programNames.note")} aria-label={t("codeBook.programNames.note")}>
            <InfoIcon />
          </span>
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-right">
          <Button
            outline
            onClick={cancel}
            className={styles.actionButton}>
            {t("common.cancel")}
          </Button>
          <Button className={styles.actionButton}
            onClick={saveChanges}
            disabled={isMinisterProgramReadonlyForUser}>
            {t("common.save")}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mt-4">
          <Form.Check
            type="switch"
            id={nameof<typeof programName>("programWithMinisterResolution")}
            label={t("codeBook.programNames.programWithMinisterResolution")}
            name={nameof<typeof programName>("programWithMinisterResolution")}
            checked={!!programName.programWithMinisterResolution}
            onChange={handleMinisterDecisionChange}
            disabled={!isHelpdesk}
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mt-5">
          <StaticField
            label={t("codeBook.programCode")}
            value={location.state?.programName?.code ?? ""}
          />
        </Col>
        <Col md={8} className="mt-5">
          <TextField
            name={nameof<typeof programName>("name")}
                label={t("codeBook.programName")}
            onChange={handleChange(setProgramName)}
            value={programName.name}
            maxLength={512}
            errorText={errors?.name}
          />
        </Col>
      </Row>
      <Row className={styles.dataRow}>
        {!programName.programWithMinisterResolution && (
          <Col md={4}>
            <Select
              label={t("codeBook.programType")}
              name={nameof<typeof programName>("programTypeId")}
              value={programName.programTypeId}
              readonly={isProgramEditMode}
              onChange={handleProgramTypeChange}
              selectData={codeBookToSelectData(filteredProgramTypes())}
              errorText={errors?.programTypeId}
            />
          </Col>
        )}
        <Col md={4}>
          <Select
            label={t("codeBook.qualification")}
            name={nameof<typeof programName>("qualificationTypeId")}
            value={programName.qualificationTypeId}
            readonly={isProgramEditMode}
            onChange={handleQualificationTypeChange}
            selectData={getQualificationTypesSelectData()}
            errorText={errors?.qualificationTypeId}
          />
        </Col>
        {!programName.programWithMinisterResolution && hkoLevels && hkoLevels.length > 2 && (
          <Col md={4}>
            <Select
              label={t("request.hkoLevel")}
              name={nameof<typeof programName>("hkoLevelId")}
              value={programName.hkoLevelId}
              readonly={isProgramEditMode}
              onChange={handleChange(setProgramName)}
              selectData={codeBookToSelectData(hkoLevels)}
              errorText={errors?.hkoLevelId}
            />
          </Col>
        )}
        {!programName.programWithMinisterResolution && (!hkoLevels || hkoLevels.length < 2) && (
          <Col md={4}>
            <StaticField
              label={t("request.hkoLevel")}
              value={programName.hkoLevelName}
            />
          </Col>
        )}
        {programName.programWithMinisterResolution && (
          <Col md={4}>
            <TextField
              label={t("request.hkoLevel")}
              name="ministerDetail.hkoLevelText"
              value={programName.ministerDetail?.hkoLevelText ?? ""}
              onChange={handleNestedChange(setProgramName)}
              errorText={getErrorForPath("ministerDetail.hkoLevelText")}
              readonly={isProgramEditMode}
            />
          </Col>
        )}
      </Row>

      <Row>
        {!programName.programWithMinisterResolution && (
          <Col md={4}>
            <Select
              label={t("common.educationSector")}
              name={nameof<typeof programName>("educationSectorId")}
              value={programName.educationSectorId}
              readonly={isProgramEditMode}
              onChange={handleSectorChange}
              selectData={codeBookToSelectData(educationSectors)}
              errorText={errors?.educationSectorId}
            />
          </Col>
        )}
        {!programName.programWithMinisterResolution && programName && programName.qualificationTypeId !== QualificationType.MicroQualification && (
          <Col md={4}>
            <Select
              label={t("common.educationSubSector")}
              name={nameof<typeof programName>("educationSubSectorId")}
              value={programName.educationSubSectorId}
              readonly={isProgramEditMode}
              onChange={handleChange(setProgramName)}
              selectData={codeBookToSelectData(educationSubSectors)}
              errorText={errors?.educationSubSectorId}
            />
          </Col>
        )}
        {programName.programWithMinisterResolution && (
          <>
            <Col md={4}>
              <TextField
                label={t("common.educationSector")}
                name="ministerDetail.educationSectorText"
                value={programName.ministerDetail?.educationSectorText ?? ""}
                onChange={handleNestedChange(setProgramName)}
                errorText={getErrorForPath("ministerDetail.educationSectorText")}
              />
            </Col>
            <Col md={4}>
              <TextField
                label={t("common.educationSubSector")}
                name="ministerDetail.educationSubSectorText"
                value={programName.ministerDetail?.educationSubSectorText ?? ""}
                onChange={handleNestedChange(setProgramName)}
                errorText={getErrorForPath("ministerDetail.educationSubSectorText")}
              />
            </Col>
          </>
        )}
      </Row>

      {programName.programTypeId === ProgramType.ForeignLanguageLearningPrograms &&
        <Row>
          <Col md={4}>
            <Select
              label={t("common.language")}
              name={nameof<typeof programName>("languageId")}
              value={programName.languageId}
              readonly={isProgramEditMode}
              onChange={handleChange(setProgramName)}
              selectData={codeBookToSelectData(languages)}
            />
          </Col>
        </Row>
      }
      {programName.programWithMinisterResolution && (
        <ProgramNameMinisterFieldsEdit
          programName={programName}
          setProgramName={setProgramName}
          readonly={isMinisterProgramReadonlyForUser}
          getErrorForPath={getErrorForPath}
          formsOfTeaching={formsOfTeaching}
          knowledgeChecks={knowledgeChecks}
          enrollmentConditions={enrollmentConditions}
          onMinisterMultiSelectChange={onMinisterMultiSelectChange}
          handleChange={handleNestedChange}
        />
      )}
      <Row>
        <Col md={4} className="mt-5">
          <DatePicker
            name={nameof<typeof programName>("dateTo")}
            label={t("common.dateTo")}
            value={programName.dateTo}
            onChange={handleChange(setProgramName)}
            errorText={errors?.dateTo}
            isClearable={true}
            minYear={new Date().getFullYear() - 25}
            maxYear={new Date().getFullYear() + 25}
          />
        </Col>
      </Row>
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.saveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureSaveChanges")}
          </Typography>
        </div>
      </Modal>
    </>
  );
}